import { useSearchParameters } from "@/components/signup/hooks/use-search-parameters";
import { ProductKey } from "@/components/signup/utils/products";

import { useConfirmationPage } from "./use-confirmation-page";
import { useJiraTemplateChoice } from "./use-jira-template-choice";
import { CreateCloudStatus } from "./utils/types";

export type ConfirmationPageType =
    | "robots"
    | "jira-animated-provisioning-screen"
    | "tips"
    | "timeout"
    | "user-segmentation"
    | "jira-user-segmentation"
    | "jira-template-selection"
    | "error"
    | "redirect"
    | "provisioning-screen"
    | "jira-onboarding-wizard"
    | "confluence-setup-over-provisioning-loading-screen"
    | "service-collection-provisioning-screen"
    | "twc-provisioning-screen";

export const useConfirmationPageType = (
    createCloudStatus: CreateCloudStatus,
    deferRedirectView?: ConfirmationPageType
): ConfirmationPageType => {
    const { shouldShowJiraTemplateChoiceScreen } = useJiraTemplateChoice();
    const { products } = useSearchParameters();
    const {
        userSegmentationContext: {
            shouldShowUserSegmentationPage,
            userSegmentationPageType
        }
    } = useConfirmationPage();

    if (shouldShowUserSegmentationPage) {
        return userSegmentationPageType;
    }

    if (shouldShowJiraTemplateChoiceScreen) {
        return "jira-template-selection";
    }

    switch (createCloudStatus) {
        case "error":
            return "error";
        case "timeout":
            return "timeout";
        case "success":
            // Optionally let sub-view handle redirect
            if (deferRedirectView) {
                return deferRedirectView;
            }

            return "redirect";

        // "pending" status
        default:
            if (products.some(includesBitbucketOrOpsGenie)) {
                return "robots";
            } else if (products.length > 1 && products.some(includesJira)) {
                return "tips";
            } else if (
                products.length === 1 &&
                products[0] === "jira-software.ondemand"
            ) {
                return "jira-animated-provisioning-screen";
            } else if (products.some(includesServiceCollection)) {
                return "service-collection-provisioning-screen";
            } else if (
                products.length === 1 &&
                products[0] === ProductKey.TEAMWORK_COLLECTION
            ) {
                return "twc-provisioning-screen";
            } else {
                return "robots";
            }
    }
};

function includesBitbucketOrOpsGenie(text: string): boolean {
    return text.includes("bitbucket") || text.includes("opsgenie");
}

// We arent testing real product keys here, unlike what occurs in sign-up
function includesJira(text: string): boolean {
    return text.includes("jira");
}

function includesServiceCollection(text: string): boolean {
    return text.includes(ProductKey.SERVICE_COLLECTION);
}
